import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { Iconify } from 'src/components';

const defaultOptions = [
  {
    value: 'hot',
    label: 'HOT',
    emoji: '🔥',
    description: 'Highly interested, likely to book or proceed with treatment',
    variant: 'hot',
  },
  {
    value: 'warm',
    label: 'WARM',
    emoji: '☀️',
    description: 'Some interest, may need more persuasion',
    variant: 'warm',
  },
  {
    value: 'cold',
    label: 'COLD',
    emoji: '❄️',
    description: 'Not ready or not serious',
    variant: 'cold',
  },
];

// Variant-specific styles matching sample.html
const getVariantStyles = (variant, isSelected) => {
  const variants = {
    hot: {
      borderColor: isSelected ? '#22C55E' : '#EEEEEE',
      bgcolor: isSelected ? 'rgba(34,197,94,0.08)' : '#FFFFFF',
      iconBg: 'rgba(34,197,94,0.12)',
      checkColor: isSelected ? '#22C55E' : '#E0E0E0',
    },
    warm: {
      borderColor: isSelected ? '#FFAB00' : '#EEEEEE',
      bgcolor: isSelected ? 'rgba(255,171,0,0.08)' : '#FFFFFF',
      iconBg: 'rgba(255,171,0,0.12)',
      checkColor: isSelected ? '#FFAB00' : '#E0E0E0',
    },
    cold: {
      borderColor: isSelected ? '#00B8D9' : '#EEEEEE',
      bgcolor: isSelected ? 'rgba(0,184,217,0.08)' : '#FFFFFF',
      iconBg: 'rgba(0,184,217,0.12)',
      checkColor: isSelected ? '#00B8D9' : '#E0E0E0',
    },
    default: {
      borderColor: isSelected ? '#00A76F' : '#EEEEEE',
      bgcolor: isSelected ? 'rgba(0,167,111,0.06)' : '#FFFFFF',
      iconBg: '#F5F5F5',
      checkColor: isSelected ? '#00A76F' : '#E0E0E0',
    },
  };
  return variants[variant] || variants.default;
};

const InterestCards = ({
  options = defaultOptions,
  selectedValue,
  onChange,
}) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: '10px', sm: '12px' },
      }}
    >
      {options.map((option) => {
        const isSelected = selectedValue === option.value;
        const styles = getVariantStyles(option.variant || 'default', isSelected);

        return (
          <Box
            key={option.value}
            onClick={() => onChange?.(option.value)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: '12px', sm: '16px' },
              padding: { xs: '14px 16px', sm: '18px 20px' },
              border: `2px solid ${styles.borderColor}`,
              borderRadius: '12px',
              bgcolor: styles.bgcolor,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              '&:hover': {
                borderColor: isSelected ? styles.borderColor : '#E0E0E0',
                bgcolor: isSelected ? styles.bgcolor : '#F5F5F5',
              },
            }}
          >
            {/* Emoji Icon */}
            <Box
              sx={{
                fontSize: { xs: '28px', sm: '32px' },
                width: { xs: '44px', sm: '50px' },
                height: { xs: '44px', sm: '50px' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                flexShrink: 0,
                bgcolor: styles.iconBg,
              }}
            >
              {option.emoji}
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: { xs: '14px', sm: '15px' },
                  fontWeight: 700,
                  color: '#212B36',
                  display: 'block',
                  marginBottom: '4px',
                }}
              >
                {option.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '12px', sm: '13px' },
                  color: '#637381',
                  lineHeight: 1.4,
                }}
              >
                {option.description}
              </Typography>
            </Box>

            {/* Check Icon */}
            <Box
              sx={{
                color: styles.checkColor,
                transition: 'all 0.15s ease',
                display: 'flex',
                alignItems: 'center',
                '& svg': {
                  width: { xs: 22, sm: 24 },
                  height: { xs: 22, sm: 24 },
                },
              }}
            >
              <Iconify icon="mdi:check-circle-outline" width={24} height={24} />
            </Box>
          </Box>
        );
      })}
    </Box>
);

InterestCards.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      emoji: PropTypes.string,
      description: PropTypes.string,
      variant: PropTypes.oneOf(['hot', 'warm', 'cold', 'default']),
    })
  ),
  selectedValue: PropTypes.string,
  onChange: PropTypes.func,
};

export default InterestCards;
